import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { SubpageNav, SubpageFooter } from "@/components/SubpageLayout";
import MollieCheckoutModal from "@/components/payments/MollieCheckoutModal";
import {
  PricingHero, PricingTimeline, PricingProblemsTable,
  PricingGuarantee, PricingSocialProof, PricingFAQ,
} from "@/components/pricing/PricingPageBlocks";
import MarketBadge from "@/components/pricing/MarketBadge";
import MarketHintToast from "@/components/pricing/MarketHintToast";
import AiAnalysisPacks from "@/components/pricing/AiAnalysisPacks";
import PlanIncludesTable from "@/components/pricing/PlanIncludesTable";  // iter1078
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, Check, Zap, Shield, Heart, Brain,
  Sparkles, TrendingUp, ClipboardList, Target, BarChart3,
  Utensils, Lock, Flame, Star, ChevronRight,
} from "lucide-react";

/* ─── Plan Data ─── */
const PLANS = {
  bg: {
    free: {
      name: "Безплатен",
      price: "0",
      currency: "EUR",
      badge: null,
      cta: "Започнете безплатно",
      micro: "Безплатен — винаги",
      color: "#64748B",
      credits: 0,
      features: [
        "Хранителен дневник (без лимит)",
        "Проследяване на инсулиновия товар и макросите",
        "Здравни калкулатори — фибри, сън, прием на вода",
        "Базов метаболитен профил",
      ],
      excluded: ["Разговор с моделите", "Клиничен преглед на показателите", "Анализ на наследствеността", "Отчети за принтиране", "Разчитане на изследвания"],
      suited: "хора, които искат да логват храна и да следят макросите си",
    },
    core: null,
    pro: {
      name: "Pro",
      price: "19.90",
      priceAnchor: "24.90",
      priceAnnual: "199",
      monthlyEquivalentAnnual: "16.58",
      currency: "EUR",
      badge: "Най-популярен",
      badgeIcon: "Flame",
      cta: "Започнете PRO",
      micro: "Пълна метаболитна картина — висцерален мастен индекс, автоматично разчитане на изследвания и мрежата от дигитални близнаци",
      color: "#8B5CF6",
      highlight: true,
      credits: 120,
      ai_analyses_approx: 120,
      features: [
        "Дневник на храненето и симулатор на чинията",
        "Разпознаване на ястие по снимка и по рецепта",
        "Записване с глас и метаболитен панел",
        "Висцерален мастен индекс и план за намаляване",
        "Автоматично разчитане на лабораторни бланки и снимки",
        "Биологична възраст и оценка за дълголетие",
        "Панели за кръвната картина и бъбреците",
        "Оценка на риска за зъбите и венците",
        "Дигитални близнаци — кортизол, сън, памет и цикъл",
        "Клиничен преглед и карта на наследствеността",
        "Програма за здраве на гърдата",
        "Оценка на кожна лезия по снимка",
        "120 анализа на месец",
      ],
      excluded: [
        "Взаимодействие между метаболитните профили — само за лекари",
        "Биологични пътища по профил — само за лекари",
        "Програми за връзката тяло и психика — само за лекари",
      ],
      suited: "хора с метаболитни цели, биохакери, спортуващи и пациенти с хронични метаболитни промени",
      best: true,
    },
    clinicLite: {
      name: "Клиника Лайт",
      price: "49",
      priceAnchor: null,
      priceAnnual: "499",
      monthlyEquivalentAnnual: "41.58",
      currency: "EUR",
      badge: "За малки практики",
      cta: "Изберете Клиника Лайт",
      micro: "За самостоятелни лекари и малки частни практики",
      color: "#0e5c60",
      credits: 300,
      ai_analyses_approx: 300,
      features: [
        "Всичко от Pro",
        "Клинична бележка за принтиране с електронен подпис",
        "Клинична справка за всеки модул",
        "Здраве на гърдата · зъби и венци · кръвна картина · бъбреци",
        "Управление до 10 активни пациента (включени)",
        "Качване на изследвания накуп за цял месец",
        "Знак за потвърден медицински специалист",
        "300 анализа на месец",
      ],
      excluded: [
        "Клинични профили при онкологични и автоимунни заболявания — само в Клиника",
        "Взаимодействие между профилите и биологични пътища — само в Клиника",
        "Съставяне на документи за пациент — само в Клиника",
      ],
      suited: "общопрактикуващи лекари, нутриционисти и самостоятелни ендокринолози",
      priceNote: "+ 4.90 € за активен пациент над включените 10",
    },
    clinic: {
      name: "Клиника",
      price: "99",
      priceAnchor: null,
      priceAnnual: "990",
      monthlyEquivalentAnnual: "82.50",
      currency: "EUR",
      badge: "За лекари",
      cta: "Изберете Клиника",
      micro: "Спестете време, поемете повече пациенти, създайте приход",
      color: "#EF4444",
      credits: 1000,
      ai_analyses_approx: 1000,
      features: [
        "Всичко от Pro за лекаря",
        "Клинични профили при онкологични и автоимунни заболявания",
        "Взаимодействие между профилите и биологични пътища в клетката",
        "Програми за връзката тяло и психика и задълбочени клинични инструменти",
        "Клинична бележка за принтиране с електронен подпис",
        "Съставяне на персонализирани документи за пациент",
        "Управление до 25 активни пациента (включени)",
        "Отчети с логото на клиниката",
        "Качване на изследвания накуп и обобщени справки",
        "Знак за потвърден медицински специалист",
        "1000 анализа на месец",
      ],
      suited: "общопрактикуващи лекари, нутриционисти, ендокринолози, частни клиники",
      priceNote: "+ 4.90 € за активен пациент над включените 25 · корпоративни условия от 499 € на месец",
    },
  },
  en: {
    free: {
      name: "Free", price: "0", currency: "EUR", badge: null, cta: "Start for free", micro: "Free — forever", color: "#64748B", credits: 0,
      features: ["Food diary (unlimited)", "Insulin-load and macro tracking", "Health calculators — fiber, sleep, water", "Basic metabolic profile"],
      excluded: ["Chat with the models", "Clinical review of your markers", "Hereditary risk analysis", "Printable reports", "Lab result interpretation"],
      suited: "people who want to log food and track macros",
    },
    core: null,
    pro: {
      name: "Pro", price: "24.90", priceAnchor: null, priceAnnual: "249", monthlyEquivalentAnnual: "20.75", currency: "EUR", badge: "Most popular", badgeIcon: "Flame", cta: "Start Pro", micro: "Full metabolic picture — visceral fat index, automatic lab reading and the digital-twin network", color: "#8B5CF6", highlight: true, best: true, credits: 120, ai_analyses_approx: 120,
      features: [
        "Food diary and plate simulator",
        "Meal recognition by photo and by recipe",
        "Voice logging and metabolic panel",
        "Visceral fat index and reduction plan",
        "Automatic reading of lab PDFs and photos",
        "Biological age and longevity score",
        "Blood count and kidney panels",
        "Dental and gum risk assessment",
        "Digital twins — cortisol, sleep, memory and cycle",
        "Clinical review and hereditary-risk map",
        "Breast Health Programme",
        "Skin lesion assessment by photo",
        "120 analyses per month",
      ],
      excluded: [
        "Metabolic profile interactions — doctors only",
        "Biological pathways per profile — doctors only",
        "Mind–body programmes — doctors only",
      ],
      suited: "people with metabolic goals, biohackers, athletes and patients with chronic metabolic conditions",
    },
    clinicLite: {
      name: "Clinic Lite", price: "49", priceAnchor: null, priceAnnual: "499", monthlyEquivalentAnnual: "41.58", currency: "EUR", badge: "For small practices", cta: "Choose Clinic Lite", micro: "For solo doctors and small private practices", color: "#0e5c60", credits: 300, ai_analyses_approx: 300,
      features: [
        "Everything in Pro",
        "Printable clinical note with electronic signature",
        "Clinical summary for each module",
        "Breast health · dental · blood count · kidneys",
        "Up to 10 active patients (included)",
        "Bulk lab upload for the full month",
        "Verified medical professional badge",
        "300 analyses per month",
      ],
      excluded: [
        "Clinical profiles for oncology and autoimmune conditions — Clinic only",
        "Profile interactions and biological pathways — Clinic only",
        "Patient document composer — Clinic only",
      ],
      suited: "GPs, nutritionists and solo endocrinologists",
      priceNote: "+ €4.90 per active patient above the 10 included",
    },
    clinic: {
      name: "Clinic", price: "99", priceAnchor: null, priceAnnual: "990", monthlyEquivalentAnnual: "82.50", currency: "EUR", badge: "For doctors", cta: "Choose Clinic", micro: "Save time, take on more patients, generate revenue", color: "#EF4444", credits: 1000, ai_analyses_approx: 1000,
      features: [
        "Everything in Pro, for the doctor",
        "Clinical profiles for oncology and autoimmune conditions",
        "Profile interactions and cellular biological pathways",
        "Mind–body programmes and in-depth clinical tools",
        "Printable clinical note with electronic signature",
        "Personalised patient document composer",
        "Up to 25 active patients (included)",
        "Reports with your clinic's logo",
        "Bulk lab upload and summary reports",
        "Verified medical professional badge",
        "1000 analyses per month",
      ],
      suited: "GPs, nutritionists, endocrinologists, private clinics",
      priceNote: "+ €4.90 per active patient above the 25 included · Enterprise from €499/month",
    },
  },
  es: {
    free: {
      name: "Gratuito", price: "0", currency: "EUR", badge: null, cta: "Empezar gratis", micro: "Gratis — para siempre", color: "#64748B", credits: 0,
      features: ["Diario de alimentación (ilimitado)", "Seguimiento de carga insulínica y macros", "Calculadoras de salud — fibra, sueño, agua", "Perfil metabólico básico"],
      excluded: ["Chat con los modelos", "Revisión clínica de sus marcadores", "Análisis de riesgo hereditario", "Informes imprimibles", "Interpretación de análisis"],
      suited: "personas que quieren registrar su alimentación y seguir sus macros",
    },
    core: null,
    pro: {
      name: "Pro", price: "24.90", priceAnchor: null, priceAnnual: "249", monthlyEquivalentAnnual: "20.75", currency: "EUR", badge: "Más popular", badgeIcon: "Flame", cta: "Empezar Pro", micro: "Imagen metabólica completa — índice de grasa visceral, lectura automática de análisis y red de gemelos digitales", color: "#8B5CF6", highlight: true, best: true, credits: 120, ai_analyses_approx: 120,
      features: [
        "Diario de alimentación y simulador del plato",
        "Reconocimiento de platos por foto y por receta",
        "Registro por voz y panel metabólico",
        "Índice de grasa visceral y plan de reducción",
        "Lectura automática de análisis (PDF y fotos)",
        "Edad biológica y puntuación de longevidad",
        "Paneles de hemograma y riñón",
        "Evaluación de riesgo dental y periodontal",
        "Gemelos digitales — cortisol, sueño, memoria y ciclo",
        "Revisión clínica y mapa hereditario",
        "Programa de salud mamaria",
        "Evaluación de lesiones cutáneas por foto",
        "120 análisis al mes",
      ],
      excluded: [
        "Interacciones entre perfiles metabólicos — solo para médicos",
        "Vías biológicas por perfil — solo para médicos",
        "Programas mente-cuerpo — solo para médicos",
      ],
      suited: "personas con objetivos metabólicos, biohackers, deportistas y pacientes con alteraciones metabólicas crónicas",
    },
    clinicLite: {
      name: "Clínica Lite", price: "49", priceAnchor: null, priceAnnual: "499", monthlyEquivalentAnnual: "41.58", currency: "EUR", badge: "Para consultas pequeñas", cta: "Elegir Clínica Lite", micro: "Para médicos en solitario y pequeñas consultas privadas", color: "#0e5c60", credits: 300, ai_analyses_approx: 300,
      features: [
        "Todo lo de Pro",
        "Nota clínica imprimible con firma electrónica",
        "Informe clínico por cada módulo",
        "Salud mamaria · dental · hemograma · renal",
        "Hasta 10 pacientes activos (incluidos)",
        "Carga masiva de análisis para el mes completo",
        "Distintivo de profesional médico verificado",
        "300 análisis al mes",
      ],
      excluded: [
        "Perfiles clínicos oncológicos y autoinmunes — solo en Clínica",
        "Interacciones entre perfiles y vías biológicas — solo en Clínica",
        "Compositor de documentos para pacientes — solo en Clínica",
      ],
      suited: "médicos de atención primaria, nutricionistas y endocrinólogos en solitario",
      priceNote: "+ 4,90 € por paciente activo por encima de los 10 incluidos",
    },
    clinic: {
      name: "Clínica", price: "99", priceAnchor: null, priceAnnual: "990", monthlyEquivalentAnnual: "82.50", currency: "EUR", badge: "Para médicos", cta: "Elegir Clínica", micro: "Ahorre tiempo, acoja a más pacientes, genere ingresos", color: "#EF4444", credits: 1000, ai_analyses_approx: 1000,
      features: [
        "Todo lo de Pro, para el médico",
        "Perfiles clínicos oncológicos y autoinmunes",
        "Interacciones entre perfiles y vías biológicas celulares",
        "Programas mente-cuerpo y herramientas clínicas avanzadas",
        "Nota clínica imprimible con firma electrónica",
        "Compositor de documentos personalizados para pacientes",
        "Hasta 25 pacientes activos (incluidos)",
        "Informes con el logotipo de su clínica",
        "Carga masiva de análisis e informes resumen",
        "Distintivo de profesional médico verificado",
        "1000 análisis al mes",
      ],
      suited: "médicos de atención primaria, nutricionistas, endocrinólogos, clínicas privadas",
      priceNote: "+ 4,90 € por paciente activo por encima de los 25 incluidos · Empresas desde 499 €/mes",
    },
  },
};

const TX = {
  bg: {
    seo_title: "Планове", seo_desc: "Изберете нивото на персонализация за вашето здраве.",
    hero_title: "Разбери какво се случва с тялото ти \u2013 и как да го подобриш",
    hero_desc: "Получаваш персонален анализ на хранене, хормони и метаболизъм, базиран на твоите данни, изследвания и начин на живот.",
    hero_note: "Системата не поставя диагнози, а показва къде има риск и какво можеш да подобриш.",
    hero_cta: "Започнете анализ", hero_secondary: "Виж плановете",
    how_title: "Как работи — за 3 стъпки",
    how_steps: [
      {
        title: "Качваш данните си",
        desc: "Снимка на хранене, глас, лабораторен PDF или въпросник. 60 секунди.",
        items: ["Хранене със снимка · AI разпознаване", "PDF лаборатории → OCR + авто-четене", "Хормони, сън, цикъл, стрес"],
      },
      {
        title: "AI свързва 11-те системи",
        desc: "Не просто калории. Проследяваме инсулиновия товар, инсулиновия индекс и ефекта през всичките 11 физиологични системи:",
        items: ["Ендокринна · глюкоза, инсулин, кортизол", "Сърдечно-съдова · възпаление, липиди", "Нервна · когнитивна енергия, сън"],
      },
      {
        title: "Получаваш персонална пътека",
        desc: "Не общи съвети — конкретни действия, базирани на твоите данни:",
        items: ["Какво точно да ядеш днес", "Кои малки промени намаляват инсулина ти", "Кога да провериш лаборатория пак"],
      },
    ],
    why_title: "С какво сме различни",
    why_p1: "Повечето платформи броят калории. Калориите казват колко енергия има в чинията — не казват какво тялото ти прави с нея.",
    why_p2: "Nutramilo следи инсулина и единадесетте физиологични системи.",
    why_p3: "Какво получавате в замяна:",
    why_items: [
      "Инсулинов товар + индивидуален инсулинов индекс — не само глюкоза",
      "Симулатор на чинията — виждате ефекта, преди да хапнете",
      "Мрежа от дигитални близнаци — хормони, сън, цикъл и напрежение",
      "Всяка препоръка е с посочен научен източник, не с маркетинг",
    ],
    plans_title: "Изберете нивото на персонализация, което Ви е необходимо",
    plans_sub: "Разликата между плановете е в дълбочината на анализа и в сигурността, с която вземате решения",
    trial_patient: "30 дни безплатно с Pro", trial_patient_desc: "Пълен достъп до Pro — висцерален индекс, разчитане на изследвания и дигиталните близнаци. Без карта.", trial_patient_btn: "Започнете безплатно",
    trial_clinician: "30-дневен пробен период за лекари", trial_clinician_desc: "Пълен достъп до режима за лекари и всички инструменти.", trial_clinician_btn: "Регистрация като лекар",
    best_for: "Най-подходящ за повечето случаи",
    suited_label: "Подходящ за",
    per_month: "/мес",
    decision_title: "Как да изберете",
    decision_items: [
      { label: "Започвате", plan: "Безплатен" },
      { label: "Искате реална стратегия", plan: "PRO" },
      { label: "Соло-лекар или малка практика", plan: "CLINIC LITE" },
      { label: "Голяма клиника / Enterprise", plan: "CLINIC" },
    ],
    trust: ["Без дългосрочен ангажимент", "Данните са защитени (AES-256)", "Можете да прекратите по всяко време"],
    important_title: "Важно",
    important_text: "Nutramilo не поставя диагнози. Платформата подпомага разбирането на здравословното състояние и предоставя насоки, базирани на метаболитни, хормонални и поведенчески данни.",
    mission: "Nutramilo ви дава яснота как функционира вашият метаболизъм и как ежедневните ви избори влияят върху него \u2014 за да вземате информирани и устойчиви решения за здравето си.",
    final_cta: "Не сте сигурни кой план е за вас?",
    final_cta_sub: "Започнете с безплатния и надградете по всяко време",
    final_btn: "Започнете безплатно",
  },
  en: {
    seo_title: "Plans", seo_desc: "Choose the personalization level for your health.",
    hero_title: "Understand what's happening with your body \u2013 and how to improve it",
    hero_desc: "Get a personalized analysis of nutrition, hormones, and metabolism, based on your data, test results, and lifestyle.",
    hero_note: "The system doesn't diagnose \u2013 it shows where the risks are and what you can improve.",
    hero_cta: "Start Analysis", hero_secondary: "See plans",
    how_title: "How It Works — in 3 steps",
    how_steps: [
      {
        title: "Upload your data",
        desc: "Meal photo, voice, lab PDF, or short questionnaire. Takes 60 seconds.",
        items: ["Photo meals · AI recognises food", "Lab PDFs → automatic reading", "Hormones, sleep, cycle, stress"],
      },
      {
        title: "AI connects the 11 systems",
        desc: "Not just calories. We track insulin load, insulin index and the effect across all 11 physiological systems:",
        items: ["Endocrine · glucose, insulin, cortisol", "Cardiovascular · inflammation, lipids", "Nervous · cognitive energy, sleep"],
      },
      {
        title: "Get a personal pathway",
        desc: "Not generic advice — specific actions grounded in your data:",
        items: ["What exactly to eat today", "Which small swaps lower your insulin", "When to recheck a lab"],
      },
    ],
    why_title: "What makes us different",
    why_p1: "Most platforms count calories. Calories tell you how much energy is on the plate — not what your body does with it.",
    why_p2: "Nutramilo looks at insulin and the 11 physiological systems.",
    why_p3: "What you get in return:",
    why_items: [
      "Insulin load and individual insulin index — not just glucose",
      "Plate simulator — see the effect before you eat",
      "Digital-twin network — hormones, sleep, cycle, stress",
      "Every recommendation has a scientific source, not marketing",
    ],
    plans_title: "Choose the level of personalisation you need",
    plans_sub: "The difference between plans is the depth of analysis and the confidence you can have in your decisions",
    trial_patient: "30-day Pro trial", trial_patient_desc: "Full Pro access — visceral index, lab auto-read and digital twins. No card.", trial_patient_btn: "Start for free",
    trial_clinician: "30-day trial for doctors", trial_clinician_desc: "Full access to the doctor's mode and all tools.", trial_clinician_btn: "Register as doctor",
    best_for: "Best choice for most cases",
    suited_label: "Suited for",
    per_month: "/mo",
    decision_title: "How to choose",
    decision_items: [
      { label: "Getting started", plan: "Free" },
      { label: "Want a real strategy", plan: "Pro" },
      { label: "Solo doctor or small practice", plan: "Clinic Lite" },
      { label: "Large clinic / Enterprise", plan: "Clinic" },
    ],
    trust: ["No long-term commitment", "Data is protected", "Cancel at any time"],
    important_title: "Important",
    important_text: "Nutramilo does not diagnose. The platform supports understanding of your health status and offers guidance based on metabolic, hormonal and behavioural data.",
    mission: "Nutramilo gives you clarity on how your metabolism works and how your daily choices affect it \u2014 so you can make informed and sustainable decisions about your health.",
    final_cta: "Not sure which plan is right for you?",
    final_cta_sub: "Start with the free plan and upgrade whenever you're ready",
    final_btn: "Start for free",
  },
  es: {
    seo_title: "Planes", seo_desc: "Elija el nivel de personalización para su salud.",
    hero_title: "Comprenda qué le ocurre a su cuerpo \u2013 y cómo mejorarlo",
    hero_desc: "Reciba un análisis personalizado de nutrición, hormonas y metabolismo, basado en sus datos, análisis y estilo de vida.",
    hero_note: "El sistema no diagnostica \u2013 muestra dónde hay riesgo y qué puede mejorar.",
    hero_cta: "Iniciar análisis", hero_secondary: "Ver planes",
    how_title: "Cómo funciona — en 3 pasos",
    how_steps: [
      {
        title: "Suba sus datos",
        desc: "Foto de comida, voz, PDF de análisis o un breve cuestionario. 60 segundos.",
        items: ["Comidas por foto · la IA reconoce los alimentos", "PDF de análisis → lectura automática", "Hormonas, sueño, ciclo, estrés"],
      },
      {
        title: "La IA conecta los 11 sistemas",
        desc: "No solo calorías. Seguimos la carga insulínica, el índice insulínico y el efecto sobre los 11 sistemas fisiológicos:",
        items: ["Endocrino · glucosa, insulina, cortisol", "Cardiovascular · inflamación, lípidos", "Nervioso · energía cognitiva, sueño"],
      },
      {
        title: "Reciba un camino personal",
        desc: "No consejos genéricos — acciones concretas basadas en sus datos:",
        items: ["Qué comer hoy exactamente", "Qué pequeños cambios bajan su insulina", "Cuándo repetir un análisis"],
      },
    ],
    why_title: "Qué nos diferencia",
    why_p1: "La mayoría de las plataformas cuentan calorías. Las calorías dicen cuánta energía hay en el plato — no lo que su cuerpo hace con ella.",
    why_p2: "Nutramilo observa la insulina y los 11 sistemas fisiológicos.",
    why_p3: "Lo que recibe a cambio:",
    why_items: [
      "Carga insulínica e índice insulínico individual — no solo glucosa",
      "Simulador del plato — vea el efecto antes de comer",
      "Red de gemelos digitales — hormonas, sueño, ciclo, estrés",
      "Cada recomendación tiene una fuente científica, no marketing",
    ],
    plans_title: "Elija el nivel de personalización que necesita",
    plans_sub: "La diferencia entre planes es la profundidad del análisis y la seguridad con la que tomará decisiones",
    trial_patient: "30 días de Pro gratis", trial_patient_desc: "Acceso completo a Pro — índice visceral, lectura de análisis y gemelos digitales. Sin tarjeta.", trial_patient_btn: "Empezar gratis",
    trial_clinician: "Prueba de 30 días para médicos", trial_clinician_desc: "Acceso completo al modo médico y a todas las herramientas.", trial_clinician_btn: "Registrarse como médico",
    best_for: "La mejor opción en la mayoría de los casos",
    suited_label: "Adecuado para",
    per_month: "/mes",
    decision_title: "Cómo elegir",
    decision_items: [
      { label: "Está empezando", plan: "Gratuito" },
      { label: "Quiere una estrategia real", plan: "Pro" },
      { label: "Médico en solitario o consulta pequeña", plan: "Clínica Lite" },
      { label: "Gran clínica / Empresa", plan: "Clínica" },
    ],
    trust: ["Sin compromiso a largo plazo", "Datos protegidos", "Cancele cuando quiera"],
    important_title: "Importante",
    important_text: "Nutramilo no diagnostica. La plataforma ayuda a comprender el estado de salud y ofrece orientación basada en datos metabólicos, hormonales y conductuales.",
    mission: "Nutramilo le da claridad sobre cómo funciona su metabolismo y cómo le afectan sus decisiones diarias \u2014 para que pueda tomar decisiones informadas y sostenibles sobre su salud.",
    final_cta: "¿No está seguro de qué plan le conviene?",
    final_cta_sub: "Empiece con el plan gratuito y pase a premium cuando quiera",
    final_btn: "Empezar gratis",
  },
};

/* ─── Step Card ─── */
function StepCard({ num, title, desc, items, icon: Icon, isDark }) {
  const colors = ["#0EA5E9", "#8B5CF6", "#10B981"];
  const c = colors[num - 1];
  return (
    <div className={`relative p-5 rounded-2xl border ${isDark ? "bg-[hsl(223,47%,8%)] border-[hsl(223,47%,16%)]" : "bg-white border-slate-100 shadow-sm"}`} data-testid={`how-step-${num}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold text-white" style={{ backgroundColor: c }}>{num}</div>
        <Icon className="w-5 h-5" style={{ color: c }} />
      </div>
      <h3 className={`text-base font-bold mb-1.5 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{title}</h3>
      <p className={`text-sm mb-2 ${isDark ? "text-slate-400" : "text-slate-500"}`}>{desc}</p>
      {items && <ul className="space-y-1.5">{items.map(it => (
        <li key={it} className="flex items-start gap-2">
          <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: c }} />
          <span className={`text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>{it}</span>
        </li>
      ))}</ul>}
    </div>
  );
}

/* ─── Plan Card ─── */
function PlanCard({ plan, isDark, navigate, t, isMobile, user, API, bg, L, billingPeriod, mollieEnabled, market }) {
  const [checkoutLoading, setCheckoutLoading] = React.useState(false);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [mollieModalOpen, setMollieModalOpen] = React.useState(false);

  const handleClinicPreview = plan.name !== "Clinic" ? null : async () => {
    if (!user) {
      navigate("/login?redirect=/pricing");
      return;
    }
    // iter690: Stripe премахнат — отваряме Wise modal с clinic preview pricing
    setMollieModalOpen(true);
  };

  const planKey = (plan.name || "").toLowerCase();
  const isClinic = planKey === "clinic";
  const isFreePlan = plan.price === "0" || plan.credits === 0;
  const isAnnual = billingPeriod === "annual" && plan.priceAnnual;
  const displayPrice = isAnnual ? plan.monthlyEquivalentAnnual : plan.price;
  const strikePrice = isAnnual ? plan.price : null;

  const handleCtaClick = async () => {
    // Free / Assessment flow for non-authenticated or free plan
    if (!user) {
      navigate(isClinic ? "/register" : "/assessment");
      return;
    }
    if (isFreePlan) { navigate("/dashboard"); return; }

    // iter690: Wise е единственият checkout flow (Stripe премахнат).
    setMollieModalOpen(true);
  };

  const isHighlight = plan.highlight;
  return (
    <div className={`relative flex flex-col p-5 rounded-2xl border transition-all ${
      isHighlight
        ? `border-2 ${isDark ? "bg-[hsl(223,47%,10%)] border-[#8B5CF6]/60 shadow-xl shadow-purple-900/30" : "bg-white border-[#8B5CF6]/50 shadow-xl shadow-purple-200/60"} ${isMobile ? "" : "sm:-mt-3 sm:pt-7 z-10"}`
        : `${isDark ? "bg-[hsl(223,47%,8%)] border-[hsl(223,47%,16%)]" : "bg-white border-slate-100 shadow-sm"}`
    }`} data-testid={`plan-${plan.name.toLowerCase()}`}>
      {/* Badge */}
      {plan.badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="text-[10px] px-3 py-1 rounded-full text-white font-semibold whitespace-nowrap flex items-center gap-1"
            style={{ backgroundColor: plan.color }}>
            {plan.badgeIcon === "Flame" && <Flame className="w-3 h-3" />}
            {plan.badge}
          </span>
        </div>
      )}

      {/* Name + Price */}
      <h3 className={`text-lg font-bold mt-1 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{plan.name}</h3>
      <div className="flex items-baseline gap-2 mt-2 mb-1">
        {/* iter382 — PPP anchor: show EU standard price crossed out when BG market gets a discount */}
        {plan.priceAnchor && !isAnnual && (
          <span
            className={`text-sm line-through ${isDark ? "text-slate-600" : "text-slate-400"}`}
            data-testid={`plan-anchor-${plan.name.toLowerCase()}`}
            title={L("Стандартна EU цена", "Standard EU price", "Precio estándar UE")}
          >
            €{plan.priceAnchor}
          </span>
        )}
        <span className={`text-3xl font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`} data-testid={`plan-price-${plan.name.toLowerCase()}`}>
          &euro;{displayPrice}
        </span>
        {displayPrice !== "0" && (
          <span className={`text-sm ${isDark ? "text-slate-500" : "text-slate-400"}`}>{t.per_month}</span>
        )}
        {strikePrice && (
          <span className={`text-sm line-through ${isDark ? "text-slate-600" : "text-slate-400"}`} data-testid={`plan-strike-${plan.name.toLowerCase()}`}>
            &euro;{strikePrice}
          </span>
        )}
      </div>
      {/* iter382 — PPP rationale line (legally clean — no "BG special" wording) */}
      {plan.priceAnchor && !isAnnual && (
        <p className={`text-[11px] mb-1 ${isDark ? "text-sky-400" : "text-sky-600"}`} data-testid={`plan-ppp-${plan.name.toLowerCase()}`}>
          {L("Адаптирана цена за вашия регион", "Regional pricing", "Precio adaptado a su región")}
        </p>
      )}
      {isAnnual && plan.priceAnnual && (
        <p className={`text-[11px] mb-3 ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>
          {L(`€${plan.priceAnnual} годишно · спестявате 17%`, `€${plan.priceAnnual}/year · save 17%`, `${plan.priceAnnual} €/año · ahorre un 17 %`)}
        </p>
      )}
      {!isAnnual && !plan.priceAnchor && displayPrice !== "0" && (
        <div className="h-[17px] mb-3" />
      )}

      {/* Value-first pill — AI analyses count, no credit numbers exposed */}
      {typeof plan.credits === "number" && plan.credits > 0 && (
        <div
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold mb-4 w-fit ${isDark ? "bg-[#F59E0B]/15 text-[#FBBF24]" : "bg-amber-50 text-amber-700 ring-1 ring-amber-200"}`}
          data-testid={`plan-value-${plan.name?.toLowerCase()}`}
        >
          <Zap className="w-3 h-3" />
          {plan.ai_analyses_approx || plan.credits}
          <span>{L(" анализа/мес", " analyses/mo", " análisis/mes")}</span>
        </div>
      )}
      {typeof plan.credits === "number" && plan.credits === 0 && (
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold mb-4 w-fit ${isDark ? "bg-slate-800 text-slate-400" : "bg-slate-100 text-slate-500"}`}>
          <Lock className="w-3 h-3" />
          {L("Само логване на храна", "Food logging only", "Solo registro de comidas")}
        </div>
      )}

      {/* Best choice tag for PRO */}
      {plan.best && (
        <p className={`text-[11px] font-semibold mb-3 flex items-center gap-1`} style={{ color: plan.color }}>
          <Star className="w-3 h-3" fill="currentColor" /> {t.best_for}
        </p>
      )}

      {/* Features */}
      <ul className="space-y-2 mb-4 flex-1">
        {plan.features.map(f => (
          <li key={f} className="flex items-start gap-2">
            <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: plan.color }} />
            <span className={`text-[13px] leading-snug ${isDark ? "text-slate-300" : "text-slate-600"}`}>{f}</span>
          </li>
        ))}
      </ul>

      {/* "Not in Pro" upsell mini-strip (Hybrid — list + Upgrade CTA) */}
      {plan.excluded && plan.excluded.length > 0 && (
        <div
          className={`rounded-lg border px-3 py-2 mb-3 ${
            isDark ? "bg-rose-500/5 border-rose-500/20" : "bg-rose-50/60 border-rose-100"
          }`}
          data-testid={`plan-excluded-${plan.name.toLowerCase()}`}
        >
          <p className={`text-[10px] font-bold mb-1 ${isDark ? "text-rose-300" : "text-rose-700"}`}>
            {L("Налично в плана за лекари", "Available in the doctor plan", "Disponible en el plan para médicos")}
          </p>
          <ul className="space-y-0.5">
            {plan.excluded.map(f => (
              <li key={f} className="flex items-start gap-1.5">
                <Lock className={`w-2.5 h-2.5 mt-[3px] flex-shrink-0 ${isDark ? "text-rose-400" : "text-rose-500"}`} />
                <span className={`text-[11px] leading-snug ${isDark ? "text-slate-400" : "text-slate-500"}`}>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Price note for Clinic */}
      {plan.priceNote && (
        <p className={`text-[10px] mb-3 ${isDark ? "text-slate-500" : "text-slate-400"}`}>{plan.priceNote}</p>
      )}

      {/* Suited for */}
      <p className={`text-[10px] mb-4 ${isDark ? "text-slate-500" : "text-slate-400"}`}>
        <span className="font-semibold">{t.suited_label}:</span> {plan.suited}
      </p>

      {/* CTA */}
      <Button onClick={handleCtaClick} disabled={checkoutLoading}
        data-testid={`select-${plan.name.toLowerCase()}-plan`}
        className={`w-full h-11 rounded-xl text-sm font-semibold text-white ${isHighlight ? "shadow-lg" : ""}`}
        style={{ background: isHighlight ? `linear-gradient(135deg, ${plan.color}, ${plan.color}cc)` : plan.color }}>
        {checkoutLoading ? L("Зарежда...", "Loading...", "Cargando...") : plan.cta} <ArrowRight className="w-4 h-4 ml-1.5" />
      </Button>

      {/* Clinic Preview €9.99 7-day taste — only on Clinic card (Iter 258) */}
      {plan.name === "Clinic" && handleClinicPreview && (
        <button
          type="button"
          onClick={handleClinicPreview}
          disabled={previewLoading}
          data-testid="clinic-preview-99-cta"
          className={`w-full mt-2 px-3 py-2 rounded-lg text-[11.5px] font-semibold border-2 border-dashed transition-all ${
            isDark
              ? "border-rose-500/40 bg-rose-500/5 text-rose-200 hover:bg-rose-500/10"
              : "border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100"
          }`}
        >
          {previewLoading
            ? L("Зарежда...", "Loading...", "Cargando...")
            : L(
                "Или · Опитай за €9.99 — 7 дни Clinic Preview →",
                "Or · Try for €9.99 — 7-day Clinic Preview →",
                "O · Pruebe por 9,99 € — 7 días Clinic Preview →"
              )}
        </button>
      )}

      {/* Microcopy */}
      <p className={`text-center text-[10px] mt-2 ${isDark ? "text-slate-600" : "text-slate-400"}`}>{plan.micro}</p>

      {mollieModalOpen && (
        <MollieCheckoutModal
          plan={plan}
          billingPeriod={billingPeriod}
          market={market}
          isDark={isDark}
          onClose={() => setMollieModalOpen(false)}
        />
      )}
    </div>
  );
}

/* ─── Main Page ─── */
export default function PricingPage() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const { user, API } = useAuth();
  const navigate = useNavigate();
  const t = TX[language] || TX.en;
  const plans = PLANS[language] || PLANS.en;
  const bg = language === "bg";
  const L = React.useCallback((bgText, enText, esText) => {
    if (language === "bg") return bgText;
    if (language === "es") return esText !== undefined && esText !== null ? esText : enText;
    return enText;
  }, [language]);
  const [billingPeriod, setBillingPeriod] = React.useState("monthly"); // "monthly" | "annual"
  const [mollieEnabled, setMollieEnabled] = React.useState(false);
  const [singleUnlockOpen, setSingleUnlockOpen] = React.useState(false);
  // iter382/iter387 — fetch geo-aware catalog (resolves market via CloudFlare header)
  // and allow manual market override (EU Reg 2018/302 compliance).
  const [founder, setFounder] = React.useState(null);
  const [market, setMarket] = React.useState(null);
  const [apiPlans, setApiPlans] = React.useState(null);  // map keyed by plan id
  // iter388 — `autoDetectedMarket` is set ONCE on first load and never changes when
  // the user manually switches market. The hint toast renders only when the
  // resolved auto-detected market is non-EU (eu == silent default).
  const [autoDetectedMarket, setAutoDetectedMarket] = React.useState(null);

  // Discover whether admin has configured Wise — if yes, silently route checkout through Wise
  React.useEffect(() => {
    axios.get(`${API}/payments/mollie/public-info`)
      .then(r => setMollieEnabled(!!r.data?.enabled))
      .catch(() => setMollieEnabled(false));
  }, [API]);

  // iter382/iter387 — fetch founder availability + resolved market + per-market prices
  React.useEffect(() => {
    const params = market ? `?market=${market}` : "";
    axios.get(`${API}/pricing/catalog${params}`)
      .then(r => {
        setFounder(r.data?.founder || null);
        if (!market) {
          setMarket(r.data?.market || null);
          setAutoDetectedMarket(r.data?.market || null);  // iter388 — captured once
        }
        const planMap = {};
        (r.data?.plans || []).forEach(p => { planMap[p.id] = p; });
        setApiPlans(planMap);
      })
      .catch(() => {});
  }, [API, market]);

  const handleCTA = () => navigate(user ? "/dashboard" : "/assessment");
  const scrollToPlans = () => document.getElementById("plans-section")?.scrollIntoView({ behavior: "smooth" });

  const sectionBg = isDark ? "bg-[hsl(223,47%,6%)]" : "bg-[#F9F9F7]";
  const altBg = isDark ? "bg-[hsl(223,47%,8%)]" : "bg-white";
  const heading = `text-xl sm:text-2xl font-bold tracking-tight ${isDark ? "text-white" : "text-[#1A1A2E]"}`;
  const body = `text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`;
  const badge = `inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-medium ${isDark ? "border-[hsl(223,47%,20%)] text-slate-400" : "border-slate-200 text-slate-500"}`;

  return (
    <div className={`min-h-screen ${sectionBg}`} data-testid="pricing-page" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <SEO title={t.seo_title} description={t.seo_desc} path="/pricing" />
      <SubpageNav />

      {/* ─── Hero ─── */}
      <section className={`relative overflow-hidden ${altBg}`} data-testid="pricing-hero-section">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="max-w-3xl mx-auto px-4 py-16 sm:py-20 relative">
          <PricingHero />
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={handleCTA} size="lg" data-testid="hero-cta-btn"
              className="bg-gradient-to-r from-[#0EA5E9] to-[#6366F1] hover:opacity-90 text-white text-sm font-semibold rounded-full px-8 h-12 shadow-lg shadow-sky-500/20">
              {t.hero_cta} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button variant="outline" size="lg" onClick={scrollToPlans} data-testid="hero-secondary-btn"
              className={`rounded-full px-8 h-12 text-sm font-medium ${isDark ? "border-slate-700 text-slate-300" : "border-slate-200 text-slate-600"}`}>
              {t.hero_secondary}
            </Button>
          </div>

          {/* Trust score badge — iter393 */}
          <div className="mt-8 flex justify-center">
            <Link to="/scientific-formulas" data-testid="pricing-trust-badge" className={`group inline-flex items-center gap-2.5 px-4 py-2 rounded-full border transition-all ${
              isDark ? "border-[hsl(223,47%,18%)] bg-[hsl(223,47%,9%)] hover:border-[hsl(223,47%,30%)]" : "border-slate-200 bg-white hover:border-slate-300 shadow-sm"
            }`}>
              <span className="flex h-2 w-2 rounded-full" style={{ background: "#287a2e" }} />
              <span className={`text-xs font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                {L(
                  "Стъпва на над 10 научни бази и милиони рецензирани публикации",
                  "Backed by 10+ peer-reviewed databases · 30M+ publications",
                  "Respaldado por más de 10 bases científicas · más de 30 millones de publicaciones"
                )}
              </span>
              <ArrowRight className="w-3 h-3 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── How It Works ─── */}
      <section className={`py-14 sm:py-20 ${sectionBg}`} data-testid="how-it-works-section">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className={badge}><Target className="w-3 h-3" /> {t.how_title}</div>
            <h2 className={`${heading} mt-3`}>{t.how_title}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {t.how_steps.map((step, i) => (
              <StepCard key={step.title} num={i + 1} title={step.title} desc={step.desc} items={step.items}
                icon={[ClipboardList, BarChart3, Utensils][i]} isDark={isDark} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── Why Useful ─── */}
      <section className={`py-14 sm:py-20 ${altBg}`} data-testid="why-useful-section">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center mb-8">
            <div className={badge}><TrendingUp className="w-3 h-3" /> {t.why_title}</div>
            <h2 className={`${heading} mt-3`}>{t.why_title}</h2>
          </div>
          <div className={`p-6 sm:p-8 rounded-2xl border ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,16%)]" : "bg-slate-50 border-slate-100"}`}>
            <p className={`text-sm mb-2 ${isDark ? "text-slate-300" : "text-slate-600"}`}>{t.why_p1}</p>
            <p className={`text-base font-semibold mb-3 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{t.why_p2}</p>
            <p className={`text-sm mb-3 ${isDark ? "text-slate-400" : "text-slate-500"}`}>{t.why_p3}</p>
            <ul className="space-y-2">{t.why_items.map(it => (
              <li key={it} className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#10B981]" />
                <span className={`text-sm font-medium ${isDark ? "text-slate-200" : "text-slate-700"}`}>{it}</span>
              </li>
            ))}</ul>
          </div>
        </div>
      </section>

      {/* iter383 — Timeline of value over 30 days */}
      <section className={`${altBg}`}><div className="max-w-5xl mx-auto px-4"><PricingTimeline /></div></section>

      {/* ─── Plans Section ─── */}
      <section id="plans-section" className={`py-14 sm:py-20 ${sectionBg}`} data-testid="plans-section">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-4">
            <h2 className={heading}>{t.plans_title}</h2>
            <p className={`text-sm mt-2 max-w-xl mx-auto ${isDark ? "text-slate-400" : "text-slate-500"}`}>{t.plans_sub}</p>
            {bg && (
              <div
                data-testid="bg-localization-notice"
                className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold ${
                  isDark
                    ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/20"
                    : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                }`}
              >
                <span>🇧🇬</span>
                <span>
                  Локализирана цена за БГ пазар — около 20% под стандартни EU
                  цени. Използвайте превключвателя по-долу, за да сравните.
                </span>
              </div>
            )}
          </div>

          {/* Trial Banners */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-testid="trial-banners">
            <div className={`flex items-center justify-between p-4 rounded-xl border ${isDark ? "bg-[#0EA5E9]/5 border-[#0EA5E9]/20" : "bg-sky-50 border-sky-100"}`}>
              <div><p className="text-sm font-bold text-[#0EA5E9]">{t.trial_patient}</p><p className={`text-xs mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>{t.trial_patient_desc}</p></div>
              <Button size="sm" onClick={() => navigate("/assessment")} data-testid="trial-patient-btn" className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white text-xs rounded-lg px-4 h-8 whitespace-nowrap ml-3">{t.trial_patient_btn}</Button>
            </div>
            <div className={`flex items-center justify-between p-4 rounded-xl border ${isDark ? "bg-[#10B981]/5 border-[#10B981]/20" : "bg-emerald-50 border-emerald-100"}`}>
              <div><p className="text-sm font-bold text-[#10B981]">{t.trial_clinician}</p><p className={`text-xs mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>{t.trial_clinician_desc}</p></div>
              <Button size="sm" onClick={() => navigate("/register")} data-testid="trial-clinician-btn" className="bg-[#10B981] hover:bg-[#059669] text-white text-xs rounded-lg px-4 h-8 whitespace-nowrap ml-3">{t.trial_clinician_btn}</Button>
            </div>
          </div>

          {/* iter387 — Market badge: transparent labelling of regional pricing + manual override (EU Reg 2018/302 compliance) */}
          <MarketBadge market={market} onMarketChange={setMarket} language={language} isDark={isDark} />

          {/* iter388 — First-visit hint toast (non-EU markets only, persistently dismissible) */}
          <MarketHintToast market={autoDetectedMarket} language={language} isDark={isDark} />

          {/* Monthly / Annual toggle */}
          <div className="flex justify-center mb-8" data-testid="billing-period-toggle">
            <div className={`inline-flex items-center rounded-full p-1 border ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,18%)]" : "bg-white border-slate-200 shadow-sm"}`}>              <button
                onClick={() => setBillingPeriod("monthly")}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  billingPeriod === "monthly"
                    ? "bg-gradient-to-r from-[#0EA5E9] to-[#6366F1] text-white shadow"
                    : isDark ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-900"
                }`}
                data-testid="billing-monthly-btn"
              >
                {L("Месечно", "Monthly", "Mensual")}
              </button>
              <button
                onClick={() => setBillingPeriod("annual")}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all flex items-center gap-1.5 ${
                  billingPeriod === "annual"
                    ? "bg-gradient-to-r from-[#0EA5E9] to-[#6366F1] text-white shadow"
                    : isDark ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-900"
                }`}
                data-testid="billing-annual-btn"
              >
                {L("Годишно", "Annual", "Anual")}
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                  billingPeriod === "annual"
                    ? "bg-white/25 text-white"
                    : isDark ? "bg-emerald-500/20 text-emerald-400" : "bg-emerald-100 text-emerald-700"
                }`}>
                  -17%
                </span>
              </button>
            </div>
          </div>

          {/* iter382 — Founder banner (only shown if founder slots remaining + market = bg) */}
          {founder && founder.active && founder.remaining_slots > 0 && (
            <div
              data-testid="founder-banner"
              className={`mb-8 rounded-2xl border-2 p-4 sm:p-5 flex items-center gap-3 sm:gap-4 ${
                isDark ? "border-amber-500/40 bg-amber-500/10" : "border-amber-300 bg-amber-50"
              }`}
            >
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500 shadow-md flex-shrink-0">
                <Star className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-xs uppercase tracking-wider font-bold ${isDark ? "text-amber-300" : "text-amber-800"}`}>
                  {L(founder.label_bg, founder.label_en, founder.label_en)}
                </p>
                <p className={`text-sm sm:text-base font-bold mt-0.5 ${isDark ? "text-white" : "text-amber-900"}`}>
                  {L(
                    `Pro на €${founder.founder_price_monthly} / мес. за първите ${founder.max_signups} потребителя — заключено за 12 месеца`,
                    `Pro at €${founder.founder_price_monthly}/mo for the first ${founder.max_signups} users — locked for 12 months`,
                    `Pro a ${founder.founder_price_monthly} €/mes para los primeros ${founder.max_signups} usuarios — bloqueado durante 12 meses`
                  )}
                </p>
                <p className={`text-xs mt-1 ${isDark ? "text-amber-200/70" : "text-amber-700"}`}>
                  {L(
                    `Остават: ${founder.remaining_slots} / ${founder.max_signups} места · след 12 месеца — стандартна цена €${founder.post_rollover_price_monthly}/мес.`,
                    `Remaining: ${founder.remaining_slots} / ${founder.max_signups} slots · after 12 months — standard price €${founder.post_rollover_price_monthly}/mo`,
                    `Quedan: ${founder.remaining_slots} / ${founder.max_signups} plazas · tras 12 meses — precio estándar ${founder.post_rollover_price_monthly} €/mes`
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Plan Cards — Mobile: PRO first, Desktop: normal order with PRO highlighted */}
          {/* iter387 — hydrate plan prices from API per resolved market */}
          {(() => {
            const hydrate = (plan, planId) => {
              if (!apiPlans || !apiPlans[planId]) return plan;
              const apiPlan = apiPlans[planId];
              // Override price + anchor + annual from API to reflect T1/EU/BG/T3.
              const hydrated = { ...plan };
              if (typeof apiPlan.price === "number") hydrated.price = apiPlan.price.toFixed(2).replace(/\.00$/, "");
              if (apiPlan.price_annual) hydrated.priceAnnual = String(apiPlan.price_annual);
              if (apiPlan.monthly_equivalent_annual) hydrated.monthlyEquivalentAnnual = String(apiPlan.monthly_equivalent_annual);
              // Show anchor (crossed-out EU price) only when current price is BELOW EU anchor (BG, T3)
              if (apiPlan.price_eu_standard && apiPlan.price < apiPlan.price_eu_standard) {
                hydrated.priceAnchor = String(apiPlan.price_eu_standard);
              } else {
                hydrated.priceAnchor = null;
              }
              return hydrated;
            };
            const freeH = hydrate(plans.free, "free");
            const proH = hydrate(plans.pro, "pro");
            const clinicLiteH = hydrate(plans.clinicLite, "clinic_lite");
            const clinicH = hydrate(plans.clinic, "clinic");
            return (
              <>
                <div className="grid grid-cols-1 sm:hidden gap-5">
                  <PlanCard plan={proH} isDark={isDark} navigate={navigate} t={t} user={user} API={API} bg={bg} L={L} billingPeriod={billingPeriod} mollieEnabled={mollieEnabled} market={market} isMobile />
                  <PlanCard plan={freeH} isDark={isDark} navigate={navigate} t={t} user={user} API={API} bg={bg} L={L} billingPeriod={billingPeriod} mollieEnabled={mollieEnabled} market={market} isMobile />
                  <PlanCard plan={clinicLiteH} isDark={isDark} navigate={navigate} t={t} user={user} API={API} bg={bg} L={L} billingPeriod={billingPeriod} mollieEnabled={mollieEnabled} market={market} isMobile />
                  <PlanCard plan={clinicH} isDark={isDark} navigate={navigate} t={t} user={user} API={API} bg={bg} L={L} billingPeriod={billingPeriod} mollieEnabled={mollieEnabled} market={market} isMobile />
                </div>
                <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-5 items-start">
                  <PlanCard plan={freeH} isDark={isDark} navigate={navigate} t={t} user={user} API={API} bg={bg} L={L} billingPeriod={billingPeriod} mollieEnabled={mollieEnabled} market={market} />
                  <PlanCard plan={proH} isDark={isDark} navigate={navigate} t={t} user={user} API={API} bg={bg} L={L} billingPeriod={billingPeriod} mollieEnabled={mollieEnabled} market={market} />
                  <PlanCard plan={clinicLiteH} isDark={isDark} navigate={navigate} t={t} user={user} API={API} bg={bg} L={L} billingPeriod={billingPeriod} mollieEnabled={mollieEnabled} market={market} />
                  <PlanCard plan={clinicH} isDark={isDark} navigate={navigate} t={t} user={user} API={API} bg={bg} L={L} billingPeriod={billingPeriod} mollieEnabled={mollieEnabled} market={market} />
                </div>
              </>
            );
          })()}
        </div>
      </section>

      {/* iter1078 — ясно какво включва всеки план + лимитите на пробния период */}
      <section className={`${sectionBg}`} data-testid="plan-includes-section">
        <div className="max-w-5xl mx-auto px-4"><PlanIncludesTable /></div>
      </section>

      {/* iter383 — Problems-by-plan comparison table + Guarantee badge */}
      <section className={`${altBg}`}><div className="max-w-5xl mx-auto px-4"><PricingProblemsTable /><PricingGuarantee /></div></section>

      {/* iter383 — Social proof (placeholder until live testimonials) */}
      <section className={`${sectionBg}`}><div className="max-w-5xl mx-auto px-4"><PricingSocialProof /></div></section>

      {/* ─── Micro-unlock bridge (€1.99 / 7 days) ─── */}
      <section className={`py-14 ${altBg}`} data-testid="micro-unlock-section">
        <div className="max-w-3xl mx-auto px-4">
          <div className={`rounded-2xl border-2 p-6 sm:p-8 ${isDark ? "border-amber-500/30 bg-amber-500/5" : "border-amber-200 bg-amber-50/40"}`}>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-amber-400 to-orange-500 shadow-md">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-[10px] uppercase tracking-[0.14em] font-bold mb-1 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                  {L("Не сте готови за абонамент?", "Not ready to subscribe?", "¿No está listo para suscribirse?")}
                </p>
                <h3 className={`text-lg sm:text-xl font-bold mb-1.5 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                  {L("Отключете единичен анализ за €1.99", "Unlock a single analysis for €1.99", "Desbloquee un análisis puntual por 1,99 €")}
                </h3>
                <p className={`text-sm leading-relaxed ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                  {L(
                    "Пълен достъп до премиум функциите за 7 дни — без автоматично подновяване. Идеално за тестване преди абонамент.",
                    "Full access to premium features for 7 days — no auto-renewal. Perfect for trying before you subscribe.",
                    "Acceso completo a las funciones premium durante 7 días — sin renovación automática. Perfecto para probar antes de suscribirse."
                  )}
                </p>
              </div>
              <Button
                onClick={() => {
                  if (!user) { navigate("/register"); return; }
                  // iter690: Stripe премахнат — единствен flow е Wise modal
                  setSingleUnlockOpen(true);
                }}
                data-testid="micro-unlock-cta-btn"
                className="h-11 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-semibold px-6 shadow-md whitespace-nowrap"
              >
                €1.99 <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Decision Helper ─── */}
      <section className={`py-12 ${altBg}`} data-testid="decision-helper">
        <div className="max-w-3xl mx-auto px-4">
          <h3 className={`text-lg font-bold text-center mb-6 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{t.decision_title}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {t.decision_items.map(d => (
              <div key={d.plan} className={`p-4 rounded-xl text-center border ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,16%)]" : "bg-slate-50 border-slate-100"}`}>
                <p className={`text-xs mb-1 ${isDark ? "text-slate-500" : "text-slate-400"}`}>{d.label}</p>
                <p className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{d.plan}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Trust Signals ─── */}
      <section className={`py-8 ${sectionBg}`} data-testid="trust-signals">
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-6">
            {t.trust.map(item => (
              <div key={item} className="flex items-center gap-2">
                <Shield className="w-3.5 h-3.5 text-[#10B981]" />
                <span className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Important Notice ─── */}
      <section className={`py-12 ${altBg}`} data-testid="important-notice">
        <div className="max-w-3xl mx-auto px-4">
          <div className={`p-6 rounded-2xl border ${isDark ? "bg-amber-900/10 border-amber-800/20" : "bg-amber-50 border-amber-100"}`}>
            <h3 className={`text-base font-bold mb-2 ${isDark ? "text-amber-300" : "text-amber-800"}`}>{t.important_title}</h3>
            <p className={`text-sm mb-4 ${isDark ? "text-amber-400/80" : "text-amber-700/80"}`}>{t.important_text}</p>
            <p className={`text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-600"}`}>{t.mission}</p>
          </div>
        </div>
      </section>

      {/* iter460 — AI Analysis Packs (one-time purchases for top-up credits) */}
      <AiAnalysisPacks />

      {/* iter383 — FAQ (top 6 objections) */}
      <section className={`${altBg}`}><div className="max-w-5xl mx-auto px-4"><PricingFAQ /></div></section>

      {/* ─── Final CTA ─── */}
      <section className={`py-16 sm:py-24 ${sectionBg}`} data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className={`text-xl sm:text-2xl font-bold tracking-tight mb-3 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{t.final_cta}</h2>
          <p className={`text-sm mb-6 ${isDark ? "text-slate-400" : "text-slate-500"}`}>{t.final_cta_sub}</p>
          <Button onClick={() => navigate("/assessment")} size="lg" data-testid="final-cta-btn"
            className="bg-gradient-to-r from-[#0EA5E9] to-[#6366F1] hover:opacity-90 text-white text-sm font-semibold rounded-full px-10 h-12 shadow-lg shadow-sky-500/20">
            {t.final_btn} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      <SubpageFooter />

      {singleUnlockOpen && (
        <MollieCheckoutModal
          plan={{ name: "Single analysis" }}
          billingPeriod="one_time"
          planIdOverride="single_unlock"
          market={market}
          isDark={isDark}
          onClose={() => setSingleUnlockOpen(false)}
        />
      )}
    </div>
  );
}
